import { useEffect, useState } from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, Tabs, Tab, Box,
  Typography, CircularProgress, Collapse, IconButton, Divider
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchInvoiceDetails } from '../../services/SalesService';
import { getReferences } from '../../services/SuppliesService';
import { formatDateTime } from '../../utils/DateUtils';
import { getPaymentLabel, getInvoiceStatus, getUnitById } from '../../utils/Enums';
import { getOptionalSelectionsList, getOptionalSelectionName } from '../../utils/optionalSelections';
import { formatMoney } from '../../utils/currency';

const formatPercent = (value) => {
  const numericValue = Number(value ?? 0);
  if (numericValue <= 0) {
    return 'N/A';
  }
  return `${numericValue.toFixed(2)}%`;
};

const calculateItemDiscountPercent = (item) => {
  const unitPrice = Number(item.unitPrice ?? 0);
  const discountAmount = Number(item.discountAmount ?? 0);

  if (unitPrice <= 0 || discountAmount <= 0) {
    return 0;
  }

  return (discountAmount / unitPrice) * 100;
};

const calculateInvoiceDiscountPercent = (invoice) => {
  const explicitPercent = Number(invoice?.discountPercent ?? 0);
  if (explicitPercent > 0) {
    return explicitPercent;
  }

  const subtotal = Number(invoice?.subtotal ?? 0);
  const discountAmount = Number(invoice?.discountAmount ?? 0);

  if (subtotal <= 0 || discountAmount <= 0) {
    return 0;
  }

  return (discountAmount / subtotal) * 100;
};

const ProductRow = ({ item, references, currency }) => {
  
    const [open, setOpen] = useState(false);
    const unit = item.unitPrice ?? 0;
    const discount = item.discountAmount ?? 0;
    const discountPercent = calculateItemDiscountPercent(item);
    const finalPrice = unit - discount;
    const hasUndefined = item.undefinedContent?.length > 0;
    const optionalSelections = getOptionalSelectionsList(item);
    const hasOptionals = optionalSelections.length > 0;
    const hasExpandable = hasUndefined || hasOptionals;

    return (
      <>
        <TableRow>
          <TableCell align="center" sx={{ height: 40 }}>
            {hasExpandable && (
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
          <TableCell align="center" sx={{ height: 40 }}>{item.productName ?? 'Producto'}</TableCell>
          <TableCell align="center" sx={{ height: 40 }}>{item.quantity}</TableCell>
          <TableCell align="center" sx={{ height: 40 }}>{formatMoney(unit, currency)}</TableCell>
          <TableCell align="center" sx={{ height: 40 }}>
            {formatPercent(discountPercent)}
          </TableCell>
          <TableCell align="center" sx={{ height: 40 }}>{item.discountCode ?? '-'}</TableCell>
          <TableCell align="center" sx={{ height: 40 }}>{formatMoney(finalPrice * item.quantity, currency)}</TableCell>
        </TableRow>
  
        {hasExpandable && (
          <TableRow>
            <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 6, pt: 1, pb: 2 }}>
                  {hasUndefined && (
                    <>
                      <Typography variant="subtitle2" gutterBottom> Productos utilizados: </Typography>
                      <Table size="small" sx={{ width: '95%', mb: hasOptionals ? 2 : 0 }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell sx={{ width: 40 }} align="center">Nombre</TableCell>
                            <TableCell sx={{ width: 30 }} align="center">Cantidad</TableCell>
                            <TableCell sx={{ width: 30 }} align="center">Unidad</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {item.undefinedContent.map((content, i) => (
                            <TableRow key={i}>
                              <TableCell align="center">{content.productName}</TableCell>
                              <TableCell align="center">{content.quantity}</TableCell>
                              <TableCell align="center">{getUnitById(content.unit)?.label || 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                  {hasOptionals && (
                    <>
                      <Typography variant="subtitle2" gutterBottom> Opcionales elegidos: </Typography>
                      <Table size="small" sx={{ width: '95%' }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell align="center">Nombre</TableCell>
                            <TableCell align="right">Precio extra</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {optionalSelections.map((optional, i) => {
                            const extraPrice = Number(optional.extraPrice ?? optional.extra_price ?? 0);
                            return (
                              <TableRow key={i}>
                                <TableCell align="center">{getOptionalSelectionName(optional, references)}</TableCell>
                                <TableCell align="right">
                                  {extraPrice > 0 ? `+${formatMoney(extraPrice, currency)}` : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
};  

const buildFallbackInvoice = (transaction) => {
  if (!transaction) return null;

  return {
    id: transaction.id,
    createdAt: transaction.createdAt,
    paymentMethod: transaction.paymentMethod,
    subtotal: transaction.subtotal ?? transaction.subTotal ?? transaction.total ?? 0,
    discountAmount: transaction.discountAmount ?? 0,
    total: transaction.total ?? 0,
    status: transaction.status,
    location: transaction.location ?? { id: transaction.locationId, name: transaction.locationName ?? 'N/A' },
    terminal: transaction.terminal ?? { reference: transaction.terminalCode ?? transaction.terminalReference ?? 'N/A' },
    items: [],
    company: transaction.company ?? null,
    client: transaction.client ?? null,
    _fallback: true,
  };
};

const TransactionDetailModal = ({ open, onClose, invoiceId, initialTransaction = null, enableAccountingFallback = false }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [references, setReferences] = useState([]);

  useEffect(() => {
    if (open && invoiceId) {
      setLoading(true);
      setInvoice(null);
      fetchInvoiceDetails(invoiceId, { skipGlobalErrorRedirect: enableAccountingFallback })
        .then(setInvoice)
        .catch((e) => {
          console.error('Error al obtener factura', e);
          if (enableAccountingFallback && e?.response?.status === 403) {
            setInvoice(buildFallbackInvoice(initialTransaction));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [open, invoiceId, initialTransaction, enableAccountingFallback]);

  useEffect(() => {
    const isRetailCompany = localStorage.getItem('companyType') === 'RETAIL';
    if (!open || !invoice?.location?.id || invoice?._fallback || enableAccountingFallback || isRetailCompany) {
      setReferences([]);
      return;
    }

    let cancelled = false;

    getReferences(invoice.location.id, { skipGlobalErrorRedirect: true })
      .then((data) => {
        if (!cancelled) {
          setReferences(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReferences([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoice?.location?.id]);

  const handleChangeTab = (_, newValue) => setTab(newValue);

  const posPayments = (invoice?.payments ?? []).filter((payment) => payment.pos);

  const posTabIndex = posPayments.length > 0 ? (invoice?.company ? 4 : 3) : -1;

  const renderSummary = () => (
    <Box>
      {(() => {
        const discountPercent = calculateInvoiceDiscountPercent(invoice);
        return (
          <>
      <Typography><strong>ID:</strong> {invoice.id}</Typography>
      <Typography><strong>Fecha:</strong> {formatDateTime(invoice.createdAt)}</Typography>
      <Typography><strong>Método de pago:</strong> {getPaymentLabel(invoice.paymentMethod)}</Typography>
      <Typography><strong>Subtotal:</strong> {formatMoney(invoice.subtotal, invoice.currency)}</Typography>
      <Typography><strong>Descuento:</strong> {formatPercent(discountPercent)}</Typography>
      <Typography><strong>Total:</strong> {formatMoney(invoice.total, invoice.currency)}</Typography>
      <Typography><strong>Local:</strong> {invoice.location?.name ?? 'N/A'}</Typography>
      <Typography><strong>Terminal:</strong> {invoice.terminal?.reference ?? 'N/A'}</Typography>
      <Typography><strong>Status:</strong> {getInvoiceStatus(invoice.status)}</Typography>
          </>
        );
      })()}
    </Box>
  );

  const cardTypeLabel = (type) => ({ CRE: 'Crédito', DEB: 'Débito' }[type] ?? type ?? '—');

  const renderPos = () => (
    <Box>
      {posPayments.map((payment, index) => {
        const pos = payment.pos;
        const rows = [
          ['Monto cobrado', formatMoney(payment.amount, invoice.currency)],
          ['Tipo de tarjeta', cardTypeLabel(pos.cardType)],
          ['Marca', pos.cardBrand ?? '—'],
          ['Tarjeta', pos.cardLast4 ? `**** ${pos.cardLast4}` : '—'],
          ['Emisor', pos.issuerName ?? '—'],
          ['Cuotas', pos.installments ?? '—'],
          ['Plan', pos.planName ?? '—'],
          ['Procesadora', pos.processorName ?? '—'],
          ['Autorización', pos.authorizationCode ?? '—'],
          ['Ticket', pos.ticket ?? '—'],
          ['Lote', pos.lot ?? '—'],
          ['Transacción del proveedor', pos.providerTransactionId ?? '—'],
          ['Código de respuesta', pos.responseCode ?? '—'],
          ['Comisión estimada', pos.estimatedCommission != null ? formatMoney(pos.estimatedCommission, invoice.currency) : '—'],
          ['N.º de cobro', payment.paymentTransactionId],
          ['Fecha del cobro', pos.createdAt ? formatDateTime(pos.createdAt) : '—'],
          ['Detalle', pos.summary ?? '—'],
        ];
        return (
          <Box key={payment.id ?? index} sx={{ mb: 2 }}>
            {posPayments.length > 1 && (
              <>
                <Typography variant="subtitle2" gutterBottom>Cobro {index + 1} de {posPayments.length}</Typography>
                <Divider sx={{ mb: 1 }} />
              </>
            )}
            {rows.map(([label, value]) => (
              <Typography key={label}><strong>{label}:</strong> {value}</Typography>
            ))}
          </Box>
        );
      })}
    </Box>
  );

  const renderProducts = () => (
    invoice.items.length > 0 ? (
      <Table size="small">
        <TableHead sx={{ backgroundColor: '#f9f9f9' }}>
          <TableRow>
            <TableCell align="center" sx={{ width: 40 }} />
            <TableCell align="center">Producto</TableCell>
            <TableCell align="center">Cantidad</TableCell>
            <TableCell align="center">Precio Unitario</TableCell>
            <TableCell align="center">Descuento</TableCell>
            <TableCell align="center">Código Descuento</TableCell>
            <TableCell align="center">Subtotal</TableCell>
          </TableRow>
        </TableHead>
          <TableBody>
          {invoice.items.map((item, index) => (
            <ProductRow key={index} item={item} references={references} currency={invoice.currency} />
          ))}
        </TableBody>
      </Table>
    ) : (
      <Typography color="text.secondary">
        {invoice?._fallback
          ? 'No pudimos traer el detalle completo de productos para este rol, pero el resumen de la transacción sí está disponible.'
          : 'No hay productos para mostrar.'}
      </Typography>
    )
  );

  const renderCompany = () => (
    invoice.company ? (
      <Box>
        <Typography><strong>RUT:</strong> {invoice.company.rut}</Typography>
        <Typography><strong>Razón Social:</strong> {invoice.company.name}</Typography>
        <Typography><strong>Dirección Fiscal:</strong> {invoice.company.fiscalAddress}</Typography>
      </Box>
    ) : null
  );

  const renderClient = () => (
    invoice.client ? (
      <Box>
        <Typography><strong>CI:</strong> {invoice.client.ci}</Typography>
        <Typography><strong>Nombre:</strong> {invoice.client.firstName} {invoice.client.lastName}</Typography>
        <Typography><strong>Email:</strong> {invoice.client.email}</Typography>
        <Typography><strong>Teléfono:</strong> {invoice.client.phone}</Typography>
      </Box>
    ) : (
      <Typography>No se registró cliente para esta venta.</Typography>
    )
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalle de Transacción</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : invoice ? (
          <>
            <Tabs value={tab} onChange={handleChangeTab} sx={{ mb: 2 }}>
              <Tab label="Resumen" />
              <Tab label="Productos" />
              <Tab label="Cliente" />
              {invoice.company && <Tab label="Empresa" />}
              {posPayments.length > 0 && <Tab label="POS" />}
            </Tabs>
            <Box>
              {tab === 0 && renderSummary()}
              {tab === 1 && renderProducts()}
              {tab === 2 && renderClient()}
              {invoice.company && tab === 3 && renderCompany()}
              {posTabIndex >= 0 && tab === posTabIndex && renderPos()}
            </Box>
          </>
        ) : (
          <Typography color="text.secondary">No hay información para mostrar.</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransactionDetailModal;
